import { createContext, useContext, useState, useEffect, ReactNode } from "react"
import toast from "react-hot-toast"

export interface CartItem {
  _id: string
  title: string
  image: string
  price: number
  discount?: number
  technique: string
  dimensions: string
  rotation?: number
  sizeCategory?: 'small' | 'large'
}

interface CartContextType {
  items: CartItem[]
  addToCart: (item: CartItem) => void
  removeFromCart: (id: string) => void
  clearCart: () => void
  isInCart: (id: string) => boolean
  totalItems: number
  totalPrice: number
  isDrawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem("artworkCartItems")
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem("artworkCartItems", JSON.stringify(items))
  }, [items])

  const addToCart = (item: CartItem) => {
    if (items.some((i) => i._id === item._id)) {
      toast.error("Cette oeuvre est deja dans votre panier")
      return
    }
    setItems((prev) => [...prev, item])
    toast.success(`"${item.title}" ajoute au panier`)
    setIsDrawerOpen(true)
  }

  const removeFromCart = (id: string) => {
    setItems((prev) => prev.filter((i) => i._id !== id))
  }

  const clearCart = () => {
    setItems([])
  }

  const isInCart = (id: string) => items.some((i) => i._id === id)

  const totalItems = items.length

  const totalPrice = items.reduce((sum, item) => {
    const finalPrice =
      item.discount && item.discount > 0
        ? item.price * (1 - item.discount / 100)
        : item.price
    return sum + finalPrice
  }, 0)

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        clearCart,
        isInCart,
        totalItems,
        totalPrice,
        isDrawerOpen,
        openDrawer: () => setIsDrawerOpen(true),
        closeDrawer: () => setIsDrawerOpen(false),
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
